/**
 * Two inches of stockinette, at this yarn's own gauge.
 *
 * The strand tells you what the yarn looks like. This tells you what the fabric
 * looks like, which is the thing a knitter is actually buying, and it is the one
 * drawing on the page that could not be faked with a colour and a border: the
 * stitch count across the square is the catalogue's stitch gauge and the course
 * count down it is the catalogue's row gauge, so a fingering at 28 stitches over
 * four inches shows a visibly finer fabric than an aran at 16, and the stitches come
 * out wider than they are tall the way real ones do.
 *
 * Each stitch is two legs meeting at the foot, stroked with a round cap, and each
 * course is drawn as one path rather than one path per stitch. Seven cards on a page
 * is a couple of thousand stitches, and none of them needs to be addressable.
 *
 * The lighting is the same distant lamp the strand uses, over the same turbulence,
 * so the two drawings on a card look like they are in the same room. That is the
 * only reason the filter is repeated here rather than shared: a filter belongs to
 * the document, and two of them keeps each component something you can drop anywhere
 * without a defs block following it around.
 */

import { SWATCH_BOX, SWATCH_INCHES, stitchGrid, textureSeed } from './geometry';
import { fibreCharacter, shelfEntry } from '../shop';
import type { YarnRecord } from '../catalogue';

export interface StitchSwatchProps {
  readonly yarn: YarnRecord;
}

export const StitchSwatch = ({ yarn }: StitchSwatchProps): JSX.Element => {
  const grid = stitchGrid(yarn.stsPer4in, yarn.rowsPer4in);
  const character = fibreCharacter(yarn.fibre);
  const entry = shelfEntry(yarn.id);
  const fibreId = `fabric-${yarn.id}`;

  return (
    <svg
      aria-label={`${SWATCH_INCHES} inches of stockinette in ${yarn.name}, ${entry.colourway}: ${grid.columns} stitches across and ${grid.rows} courses down.`}
      className="fabric"
      data-columns={grid.columns}
      data-courses={grid.rows}
      data-handle={character}
      role="img"
      viewBox={`0 0 ${SWATCH_BOX} ${SWATCH_BOX}`}
    >
      <defs>
        <filter id={fibreId} x="0" y="0" width="100%" height="100%">
          <feTurbulence
            baseFrequency={character === 'halo' ? '1.6 1.6' : '0.9 0.9'}
            numOctaves={2}
            result="fibres"
            seed={textureSeed(yarn.id)}
            type="fractalNoise"
          />
          <feDiffuseLighting
            className="fabric__lamp"
            diffuseConstant={1}
            in="fibres"
            surfaceScale={character === 'halo' ? 2.4 : 1.5}
          >
            <feDistantLight azimuth={228} elevation={56} />
          </feDiffuseLighting>
        </filter>
      </defs>

      {/* The gap between stitches is not the card behind them, it is the shaded
          inside of the fabric, so the square gets a ground of its own. */}
      <rect className="fabric__ground" height="100%" width="100%" x="0" y="0" />

      {grid.courses.map((course, index) => (
        // The index is the key because a course has no identity beyond its place in
        // the fabric, and the fabric is rebuilt whole whenever the gauge changes.
        <g className="fabric__course" key={index}>
          <path className="fabric__legs" d={course.legs} strokeWidth={grid.legWidth} />
          <path className="fabric__crown" d={course.sheen} strokeWidth={grid.sheenWidth} />
        </g>
      ))}

      <rect
        className="fabric__fibres"
        filter={`url(#${fibreId})`}
        height="100%"
        width="100%"
        x="0"
        y="0"
      />
    </svg>
  );
};
